/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router-dom";
import ListView from "../../components/common/ListView";
import NoDataFound from "../../components/common/NoDataFound";
import formatDate, {
  formatDateTime,
  formatDateWithTime,
} from "../../utilities/helper";
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronUp,
} from "@tabler/icons-react";
import Pagination from "../../components/common/Pagination";
import { useEffect, useState } from "react";
import { useFetchReturnToShipperDetails } from "../../apis/returnToShipperApi";
import TableSkeleton from "../../components/skeleton/TableSkeleton";
import TimelineUI from "./_components/TimelineUI";

const Section = ({
  title,
  children,
  className,
  mainClassName,
}: {
  title?: string;
  className?: string;
  mainClassName?: string;
  children: React.ReactNode;
}) => (
  <div className="bg-white shadow-sm border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-4">
    {children}
  </div>
);

const StatCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value: any;
  color: string;
}) => (
  <div
    className={`rounded-xl p-3 text-white shadow-sm flex items-center space-x-2 ${color}`}
  >
    <p className="text-xs opacity-80">{label} :</p>
    <p className="text-lg font-bold">{value || 0}</p>
  </div>
);

const InfoTile = ({ label, value }: { label: string; value: any }) => (
  <div className="bg-gray-50 hover:bg-gray-100 transition rounded-xl p-3 border  flex items-center space-x-2">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-gray-800 break-words">
      {value || ""}
    </p>
  </div>
);

const ReturnToShiperViewDetails = () => {
  const TwoCol = ({
    label,
    value,
    className,
  }: {
    label: string;
    value: string;
    className?: string;
  }) => (
    <div className="text-sm text-[#1E293B] flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-3 border-b py-3 last:border-0 px-4">
      <p className="font-medium text-[#212121] capitalize">{label}:</p>
      <p className="sm:text-end break-words">{value || ""}</p>
    </div>
  );

  const [openSection, setOpenSection] = useState<string | null>(
    "ReturnToShipperDetails",
  );
  const toggleSection = (section: string) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  const navigate = useNavigate();
  const { id } = useParams<{ id: any }>();

  const returnId = id;

  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  const {
    refetch,
    data: pickupData,
    isLoading,
    isFetching,
  } = useFetchReturnToShipperDetails(pageNo, limit, "", returnId ?? "");

  const timeLine = (pickupData?.statusHistories || [])
    .sort(
      (a: any, b: any) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )
    .map((item: any, index: number) => ({
      id: item.id || index + 1,
      label: item.newStatus
        ?.toLowerCase()
        ?.replace(/_/g, " ")
        ?.replace(/\b\w/g, (char: string) => char.toUpperCase()),
      date: formatDateWithTime(item.createdAt),
      details: item.details || "",
    }));

  useEffect(() => {
    refetch();
  }, [pageNo, limit]);

  return (
    <>
      <div className="p-4 pt-0 space-y-4">
        {/* Header */}
        <div className="flex flex-wrap gap-3 justify-between items-center bg-white p-3 sm:p-4 rounded-xl shadow-sm border">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 border border-[#D2D2D2] rounded-full hover:bg-[#F7F6F2] transition"
            >
              <IconChevronLeft strokeWidth={1.5} size={22} />
            </button>
            <div className="flex items-center gap-2">
              <h1 className="text-xl text-[#212121] tracking-[-0.5px]  ">
                View Request: {pickupData?.returnId}
              </h1>
            </div>
          </div>
          {/* <button
            className={`rounded-full w-fit px-4 py-1.5 text-xs font-medium text-center ${
              statusStyles[pickupData.status as keyof typeof statusStyles] ||
              "bg-[#EEEEEE] text-[#212121]"
            }`}
          >
            {formattedStatus}
          </button> */}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-1 gap-4 xl:gap-6 w-full">
          <TimelineUI steps={timeLine} currentStep={timeLine.length - 1} />
          <Section>
            <div
              onClick={() => toggleSection("returnToShipperDetails")}
              className="flex justify-between items-center cursor-pointer"
            >
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                📦 Return To Shipper Details
              </h2>
              <button className="p-2 border rounded-full hover:bg-gray-100 transition">
                {openSection === "returnToShipperDetails" ? (
                  <IconChevronUp size={22} stroke={1.5} color="#6B7280" />
                ) : (
                  <IconChevronDown size={22} stroke={1.5} color="#6B7280" />
                )}
              </button>
            </div>

            {openSection === "returnToShipperDetails" && (
              <div className="bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    🚚 Return Details
                  </h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                  <InfoTile label="Name" value={pickupData?.clientName} />

                  <InfoTile
                    label="No. of Packages"
                    value={pickupData?.shipmentsCount}
                  />

                  <InfoTile
                    label="Date"
                    value={formatDate(pickupData?.pickupDate)}
                  />
                  <InfoTile label="Time Slot" value={pickupData?.timeSlot} />

                  {(pickupData?.status === "ASSIGNED" ||
                    pickupData?.status === "COMPLETED" ||
                    pickupData?.status === "PENDING") && (
                    <>
                      <InfoTile
                        label="Driver Code"
                        value={pickupData?.driverCode}
                      />
                      <InfoTile
                        label="Assigned Driver"
                        value={pickupData?.driverName}
                      />
                    </>
                  )}

                  {(pickupData?.status === "COMPLETED" ||
                    pickupData?.status === "PENDING") && (
                    <>
                      <InfoTile
                        label="Warehouse Name"
                        value={pickupData?.warehouses}
                      />
                      <InfoTile
                        label="Received By"
                        value={pickupData?.staffName}
                      />
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 xl:col-span-3">
                        <StatCard
                          color="bg-gray-800"
                          label="Total"
                          value={pickupData?.shipmentsCount}
                        />
                        <StatCard
                          color="bg-green-600"
                          label="Received"
                          value={pickupData?.verifiedShipmentsCount}
                        />
                        <StatCard
                          label="Missing"
                          color="bg-yellow-500"
                          value={pickupData?.missingShipmentsCount}
                        />
                        <StatCard
                          color="bg-red-600"
                          label="Damage"
                          value={pickupData?.damageShipmentsCount}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </Section>
          {/* <Section>
            <div
              onClick={() => toggleSection("returnToShipperDetails")}
              className="flex justify-between items-center cursor-pointer"
            >
              <h2 className="text-lg font-semibold mb-4 text-gray-800">
                Return To Shipper Details
              </h2>
              <button className="p-2 border rounded-full hover:bg-gray-100 transition">
                {openSection === "returnToShipperDetails" ? (
                  <IconChevronUp size={22} stroke={1.5} color="#6B7280" />
                ) : (
                  <IconChevronDown size={22} stroke={1.5} color="#6B7280" />
                )}
              </button>
            </div>

            {openSection === "returnToShipperDetails" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-[#FCFDF8] border rounded-2xl overflow-hidden">
                  <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                    Sender Information
                  </h3>
                  <InfoTile label="Name" value={pickupData?.clientName} />
                  <InfoTile label="Phone" value={pickupData?.mobileNo} />
                  <InfoTile label="Email" value={pickupData?.email} />
                  <InfoTile
                    className="col-span-2 "
                    label="Address"
                    value={pickupData?.fullAddress}
                  />
                </div>

                <div className="bg-[#FCFDF8] border rounded-2xl overflow-hidden">
                  <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                    Shipment Details
                  </h3>
                  <InfoTile
                    className="col-span-1"
                    label="No. of Packages"
                    value={pickupData?.shipmentsCount}
                  />

                  <InfoTile label="Notes" value="Contains glass items" />
                  <InfoTile
                    label="Date"
                    value={formatDate(pickupData?.pickupDate)}
                  />
                  <InfoTile label="Time Slot" value={pickupData?.timeSlot} />
                </div>

                {(pickupData?.status === "ASSIGNED" ||
                  pickupData?.status === "COMPLETED" ||
                  pickupData?.status === "PENDING") && (
                  <div className="bg-[#FCFDF8] border rounded-2xl overflow-hidden">
                    <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                      Driver Details
                    </h3>
                    <InfoTile
                      label="Driver Code"
                      value={pickupData?.driverCode}
                    />
                    <InfoTile
                      label="Assigned Driver"
                      value={pickupData?.driverName}
                    />
                    <InfoTile label="Vehicle" value={pickupData?.vehicleType} />
                    <InfoTile
                      label="Contact"
                      value={pickupData?.driverMobileNo}
                    />
                  </div>
                )}

                {(pickupData?.status === "COMPLETED" ||
                  pickupData?.status === "PENDING") && (
                  <div className="bg-[#FCFDF8] border rounded-2xl overflow-hidden ">
                    <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                      RIW Details
                    </h3>
                    <InfoTile
                      label="Warehouse Name"
                      value={pickupData?.warehouses}
                    />
                    <InfoTile label="Received By" value={pickupData?.staffName} />

                    <InfoTile
                      label="Numbers of Shipments"
                      value={pickupData?.shipmentsCount}
                    />
                    <InfoTile
                      label="Received"
                      value={pickupData?.verifiedShipmentsCount}
                    />
                    <InfoTile
                      label="Missing"
                      value={pickupData?.missingShipmentsCount}
                    />
                    <InfoTile
                      label="Damage"
                      value={pickupData?.damageShipmentsCount}
                    />
                  </div>
                )}
              </div>
            )}
          </Section> */}

          <Section>
            <div
              onClick={() => toggleSection("ReturnToShipperShipmentLists")}
              className="flex justify-between items-center cursor-pointer"
            >
              <h2 className="text-lg font-semibold mb-4 text-gray-800">
                Return To Shipper Shipment Lists
              </h2>
              <button className="p-2 border rounded-full hover:bg-gray-100 transition">
                {openSection === "ReturnToShipperShipmentLists" ? (
                  <IconChevronUp size={22} stroke={1.5} color="#6B7280" />
                ) : (
                  <IconChevronDown size={22} stroke={1.5} color="#6B7280" />
                )}
              </button>
            </div>
            {openSection === "ReturnToShipperShipmentLists" && (
              <>
                <div className="border rounded-2xl w-full col-span-2 overflow-hidden">
                  <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4  ">
                    Shipments ({pickupData?.shipments?.length || "0"})
                  </h3>

                  {isLoading || isFetching ? (
                    <div className="">
                      <TableSkeleton
                        rows={6}
                        columns={["AWB", "Origin", "Destination", "Status"]}
                      />
                    </div>
                  ) : pickupData?.shipments?.length > 0 ? (
                    <ListView
                      mailDivClassName="w-full cols-span-2  p-0    "
                      mainDivClassName=""
                      trClassName="hover:bg-[#FBD037]/20 group"
                      tdClassName="rounded-r-none rounded-l-none"
                      data={pickupData?.shipments}
                      onRowClick={(client) => console.log("Clicked:", client)}
                      columns={[
                        { label: "AWB", render: (c: any) => c.awbNumber },
                        { label: "Origin", render: (c: any) => c.senderCity },
                        {
                          label: "Destination",
                          children: [
                            {
                              label: "Country",
                              render: (c: any) => c.receiverCountry,
                            },
                            {
                              label: "City",
                              render: (c: any) => c.receiverCity,
                            },
                            {
                              label: "District",
                              render: (c: any) => c.receiverDistrict,
                            },
                          ],
                        },
                        {
                          label: "Status",
                          render: (c) => {
                            const getStatusClasses = (status: string) => {
                              switch (status) {
                                case "Out for delivery":
                                case "Dispatch":
                                  return "bg-[#EBF7CE] text-[#212121] border border-[#C6E59D]";
                                case "Delivered":
                                  return "bg-[#EEEEEE] text-[#212121] border border-[#CFCFCF]";
                                case "Undelivered":
                                  return "bg-[#F7E9CE] text-[#212121] border border-[#E6C98A]";
                                default:
                                  return "bg-[#EEEEEE] border border-[#d2d2d2] text-[#212121]";
                              }
                            };
                            const formattedStatus = c.status
                              .toLowerCase()
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (char: string) =>
                                char.toUpperCase(),
                              );
                            return (
                              <div className="flex justify-center items-center">
                                <button
                                  className={`rounded-full px-4 py-1 text-center ${getStatusClasses(
                                    c.status,
                                  )}`}
                                >
                                  {formattedStatus}
                                </button>
                              </div>
                            );
                          },
                        },
                      ]}
                    />
                  ) : (
                    <NoDataFound />
                  )}
                </div>

                {pickupData?.totalPages > 1 && (
                  <div className="flex justify-center py-4">
                    <Pagination
                      totalPages={pickupData?.totalPages}
                      currentPage={pageNo}
                      fetchData={handlePaginationFetch}
                    />
                  </div>
                )}
              </>
            )}
          </Section>
        </div>
      </div>
    </>
  );
};

export default ReturnToShiperViewDetails;
